import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { RecordedSave, SavedBy } from "@/admin/Recorded";
import { Table } from "@/admin/AdminLayout";
import { Field, inputCls } from "@/components/common";
import { useSite } from "@/context/SiteContext";

const TEXT: [string, string][] = [["eyebrow", "Eyebrow"], ["title", "Page title"], ["submit_label", "Button label"], ["success_title", "Thank-you heading"]];
const LISTS: [string, string][] = [["occasions", "Occasions"], ["durations", "Hire lengths"], ["pieces", "Pieces"], ["budgets", "Budgets"]];

function FormSettings({ initial }: { initial: any }) {
  const { reload } = useSite() as any;
  const [f, setF] = useState<any>(initial);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/admin-x/hire/form", f); setF(r.data); reload?.(); toast("Hire form saved"); return true; }
    catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5 max-w-3xl" data-testid="hire-form-settings">
      <div className="grid sm:grid-cols-2 gap-4">
        {TEXT.map(([k, l]) => <Field key={k} label={l}><input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} data-testid={`hire-form-${k}`} /></Field>)}
      </div>
      <Field label="Intro"><textarea rows={2} value={f.intro || ""} onChange={(e) => setF({ ...f, intro: e.target.value })} className={inputCls} data-testid="hire-form-intro" /></Field>
      <Field label="Thank-you message"><textarea rows={2} value={f.success_text || ""} onChange={(e) => setF({ ...f, success_text: e.target.value })} className={inputCls} data-testid="hire-form-success_text" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        {LISTS.map(([k, l]) => (
          <Field key={k} label={`${l} (one per line)`}>
            <textarea rows={6} value={(f[k] || []).join("\n")} onChange={(e) => setF({ ...f, [k]: e.target.value.split("\n") })} className={inputCls} data-testid={`hire-form-${k}`} />
          </Field>
        ))}
      </div>
      <RecordedSave res="site" id="site" onSave={save} value={f} label="Save form" testId="hire-form-save" onRestored={() => api.get("/admin-x/hire").then((r) => { if (r.data.form) setF(r.data.form); reload?.(); })} />
    </div>
  );
}

export function HireInbox({ onChange }: { onChange?: () => void }) {
  const [d, setD] = useState<any>(null);
  const [tab, setTab] = useState("enquiries");
  const load = () => api.get("/admin-x/hire").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const status = async (id: string, s: string) => { try { await api.put(`/admin-x/hire/enquiries/${id}`, { status: s }); load(); onChange?.(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  return (
    <div data-testid="hire-inbox">
      <div className="flex gap-5 mb-4 text-xs">
        {[["enquiries", `Enquiries (${d.enquiries.length})`], ["form", "Form settings"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`pb-1 border-b ${tab === k ? "border-neutral-900" : "border-transparent text-neutral-500"}`} data-testid={`hire-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "form" ? <FormSettings initial={d.form} /> : (
        <Table head={["Received", "Contact", "Occasion", "Pieces", "Details", "Message", "Status"]} testId="hire-enquiries-table">
          {d.enquiries.map((e: any) => (
            <tr key={e.id}>
              <td className="px-4 py-3 text-xs">{new Date(e.created_at).toLocaleString("en-AU")}</td>
              <td className="px-4 py-3">{e.name}<div className="text-xs"><a href={`mailto:${e.email}`} className="underline">{e.email}</a></div><div className="text-xs">{e.phone} {e.contact_pref && `· prefers ${e.contact_pref}`}</div></td>
              <td className="px-4 py-3 text-xs">{e.occasion}{e.event_date && <div>{new Date(e.event_date + "T00:00").toLocaleDateString("en-AU")}</div>}</td>
              <td className="px-4 py-3 text-xs">{(e.pieces || []).join(", ") || "—"}</td>
              <td className="px-4 py-3 text-xs">{[e.duration, e.budget, e.location].filter(Boolean).join(" · ") || "—"}</td>
              <td className="px-4 py-3 text-xs max-w-xs">{e.message}</td>
              <td className="px-4 py-3"><select value={e.status} onChange={(x) => status(e.id, x.target.value)} className="border border-neutral-300 text-xs px-2 py-1" data-testid={`hire-enquiry-status-${e.id}`}>
                {[["new", "New"], ["contacted", "Contacted"], ["booked", "Booked"], ["closed", "Closed"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select><SavedBy d={e} testId={`hire-saved-${e.id}`} /></td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}
